'use strict';

// Bottom-fixed mobile/tablet dock shared by every tool that splits a long
// form from its result or document (ROI, charter, planner - Phase 3 adds
// the assessment tools). Progressive enhancement only: the jump/return
// targets are real in-page anchors that already work without JS, this just
// adds a shortcut on top and is invisible until this module reveals it.
//
// initToolDock({ dock, jumpTarget, returnTarget, autoVisibility, hideJumpUntilComplete }):
//   dock        - the .tool-dock element.
//   jumpTarget  - element to scroll to on click (results / document).
//   returnTarget - optional. When given, the dock becomes two-state: it
//                  shows a "View x" jump while returnTarget is in view, and
//                  flips to "Back to form" once the user has scrolled past
//                  it into jumpTarget. Without it, the dock is single-state
//                  and simply hides once jumpTarget is already visible.
//   autoVisibility - default true. Single-state docks (no returnTarget)
//                  normally show/hide themselves based on whether jumpTarget
//                  is in view. Assessment tools pass false and drive
//                  visibility themselves via show() on first engagement -
//                  there's nothing to jump to yet before that.
//   hideJumpUntilComplete - default false. Keeps the jump button hidden
//                  until setComplete(true) - for an assessment tool with
//                  nothing to show until every question is answered.
//
// Returns { setReading(text), setComplete(isComplete), show(), hide() }.
export function initToolDock({
  dock, jumpTarget, returnTarget, autoVisibility = true, hideJumpUntilComplete = false,
}) {
  if (!dock || !jumpTarget) return null;

  const readingEl = dock.querySelector('[data-dock-reading]');
  const labelEl = dock.querySelector('[data-dock-label]');
  const jumpBtn = dock.querySelector('[data-dock-jump]');
  const jumpLabel = (labelEl && labelEl.textContent) || 'View results';

  if (hideJumpUntilComplete && jumpBtn) jumpBtn.hidden = true;

  const reduceMotion = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let mode = 'jump';

  function scrollToTarget(target) {
    if (!target) return;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function applyMode() {
    if (labelEl) labelEl.textContent = mode === 'return' ? 'Back to form' : jumpLabel;
    dock.classList.toggle('is-return', mode === 'return');
  }

  function setVisible(visible) {
    dock.classList.toggle('is-visible', visible);
    document.body.classList.toggle('tool-dock-open', visible);
    dock.inert = !visible;
  }

  // Lifts the dock above an on-screen keyboard that shrinks the visual
  // viewport without shrinking the layout viewport (and so, position:fixed)
  // to match - translate by the difference. A no-op wherever the two stay
  // in sync (visualViewport unsupported, keyboard closed, or a browser/OS
  // combination that resizes the layout viewport itself instead).
  function trackKeyboard() {
    const vv = window.visualViewport;
    if (!vv) return;
    const reposition = () => {
      const lift = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      dock.style.setProperty('--dock-lift', lift ? `${lift}px` : '0px');
    };
    vv.addEventListener('resize', reposition);
    vv.addEventListener('scroll', reposition);
  }

  if (jumpBtn) {
    jumpBtn.addEventListener('click', () => {
      scrollToTarget(mode === 'return' ? returnTarget : jumpTarget);
    });
  }

  if (returnTarget && typeof IntersectionObserver === 'function') {
    // Two-state dock: always showing something useful (either target is
    // always "the other one"), so it's visible for the page's whole life -
    // only the label/mode changes as you scroll between form and document.
    setVisible(true);
    // Watches jumpTarget (the document), not returnTarget (the form) - "are
    // we AT the document" is the right question, not "are we NOT at the
    // form". Those aren't the same thing on a page with real content above
    // the form (planner's hero/intro pushes its form 1600px+ down - at
    // scroll 0 neither the form nor the document is in view, and watching
    // the form got that landed-on-neither state backwards, showing "Back
    // to form" before a visitor had ever reached the form once).
    //
    // threshold: 0 (any pixel visible counts as "there") on purpose, not a
    // higher fraction like 0.4 - these forms/documents are routinely
    // several times taller than a phone viewport, so a percentage-of-
    // total-height threshold above roughly 1/(viewport height ratio) can
    // never be satisfied at any scroll position. Found live: a 0.4
    // threshold against a form measured at ~4959px tall over an 812px
    // viewport (max possible visible fraction ~16%) never crossed 40%, so
    // mode settled on 'return' immediately on load and never moved.
    const flipObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        mode = entry.isIntersecting ? 'return' : 'jump';
        applyMode();
      }
    }, { threshold: 0 });
    flipObserver.observe(jumpTarget);
  }

  if (!returnTarget && autoVisibility && typeof IntersectionObserver === 'function') {
    const visibilityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) setVisible(!entry.isIntersecting);
    }, { threshold: 0.1 });
    visibilityObserver.observe(jumpTarget);
  }

  applyMode();
  trackKeyboard();

  return {
    setReading(text) {
      if (readingEl) readingEl.textContent = text;
    },
    setComplete(isComplete) {
      dock.classList.toggle('is-complete', !!isComplete);
      if (hideJumpUntilComplete && jumpBtn) jumpBtn.hidden = !isComplete;
    },
    show() { setVisible(true); },
    hide() { setVisible(false); },
  };
}
